// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import axios, { AxiosError } from 'axios';
import type { AdvisorProfile, AdvisorUiFeatures, BrokerageInfo } from '../types/advisor';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Extract tenant/brokerage ID from the current hostname subdomain
 * Examples:
 *  - tb3.advisor.localhost -> tb3
 *  - acme.advisor.example.com -> acme
 *  - localhost:3002 -> tb1 (fallback for local dev)
 */
const extractBrokerageIdFromHostname = (): string => {
  const hostname = globalThis.location.hostname;

  // Handle localhost for local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'tb1'; // Default fallback
  }

  // Extract subdomain (first part before first dot)
  // Example: tb3.advisor.localhost -> tb3
  const parts = hostname.split('.');
  if (parts.length > 0) {
    return parts[0];
  }

  return 'tb1'; // Fallback
};

// Store the current brokerageId (initialize from hostname)
let currentBrokerageId: string = extractBrokerageIdFromHostname();
console.log(`Current brokerageId from hostname: ${currentBrokerageId}`);

// Add request interceptor to include X-Brokerage-Id header
api.interceptors.request.use(
  (config) => {
    // Add X-Brokerage-Id header to all requests
    config.headers['X-Brokerage-Id'] = currentBrokerageId;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle authentication redirects
api.interceptors.response.use(
  (response) => {
    // If we get HTML instead of JSON, we've been redirected to a login page
    const contentType = response.headers['content-type'];
    if (typeof contentType === 'string' && contentType.includes('text/html')) {
      const returnUrl = encodeURIComponent(globalThis.location.pathname + globalThis.location.search);
      globalThis.location.href = `/login?returnUrl=${returnUrl}`;
      return Promise.reject(new Error('Redirecting to login'));
    }
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      console.log('API 401 received, redirecting to login...');
      const returnUrl = encodeURIComponent(globalThis.location.pathname + globalThis.location.search);
      globalThis.location.href = `/login?returnUrl=${returnUrl}`;
      return Promise.reject(new Error('Redirecting to login'));
    }

    // Handle network errors or other login-related errors
    if (error.code === 'ERR_NETWORK' || error.message?.includes('login')) {
      const returnUrl = encodeURIComponent(globalThis.location.pathname + globalThis.location.search);
      globalThis.location.href = `/login?returnUrl=${returnUrl}`;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const advisorApi = {
  /**
   * Set the current brokerageId for API calls
   */
  setBrokerageId: (brokerageId: string): void => {
    currentBrokerageId = brokerageId;
  },

  /**
   * Get current advisor profile
   */
  getProfile: async (): Promise<AdvisorProfile> => {
    const response = await api.get<AdvisorProfile>('/advisors/me');
    return response.data;
  },

  /**
   * Get the effective per-brokerage Advisor UI feature flags.
   * `policiesWizardStep` gates the relationship wizard's legacy
   * "Add Policies" step (off by default — single-step wizard ending
   * on the invitation-email screen).
   */
  getUiFeatures: async (): Promise<AdvisorUiFeatures> => {
    const response = await api.get<AdvisorUiFeatures>('/advisors/me/features');
    return response.data;
  },

  /**
   * Update advisor profile
   */
  updateProfile: async (data: Partial<AdvisorProfile>): Promise<AdvisorProfile> => {
    const response = await api.put<AdvisorProfile>('/advisors/me', data);
    return response.data;
  },

  /**
   * Initiate logout
   */
  logout: async (): Promise<void> => {
    globalThis.location.href = '/logout-page';
  },

  /**
   * Update advisor profile photo
   */
  updatePhoto: async (advisorId: string, photoUrl: string): Promise<AdvisorProfile> => {
    const response = await api.put<AdvisorProfile>(
      `/advisors/${advisorId}/photo`,
      { photoUrl }
    );
    return response.data;
  },

  /**
   * Remove advisor profile photo
   */
  removePhoto: async (advisorId: string): Promise<AdvisorProfile> => {
    const response = await api.delete<AdvisorProfile>(`/advisors/${advisorId}/photo`);
    return response.data;
  },

  /**
   * Get brokerage information by ID
   */
  getBrokerageInfo: async (brokerageId: string): Promise<BrokerageInfo> => {
    const response = await api.get<BrokerageInfo>(`/brokerages/${brokerageId}`);
    return response.data;
  },
};

export default api;
